/**
 * AddCustomerModal — create a new Sage customer from the portal.
 *
 * Creates the REAL account: the customer number is allocated server-side
 * (name prefix + next sequence, the CARRO01 convention) and the account is
 * pushed through Adam's audited cloud->Sage customer path. It lands in Sage
 * within ~2 minutes (reconciler-confirmed, red-flagged if it never lands).
 *
 * Reused by every add-customer surface: the Customers page button, the
 * customer typeahead in the manual-order dialog, and the inbox
 * "Potential New Customer?" flow (which pre-fills name/email).
 *
 * Where the Edit-in-Sage gate is off (local dev), the form is replaced by a
 * short "not enabled in this environment" note.
 */

import { useEffect, useState } from 'react';
import { portalService } from '../../services/portal';
import type { SalespersonOption } from '../../types/portal';
import { findInternalAddresses, isInternalEmail } from '../../utils/internalEmail';
import styles from './AddCustomerModal.module.css';

interface AddCustomerModalProps {
  /** Pre-fill the search query as customer name */
  initialName?: string;
  /** Pre-fill the email field from the sender address */
  initialEmail?: string;
  /** Billing-address / contact prefills (e.g. Merlin's proposed new customer). */
  initialAddress1?: string;
  initialAddress2?: string;
  initialCity?: string;
  initialState?: string;
  initialZip?: string;
  initialPhone?: string;
  /** Fired from the success state; ``customerName`` is the name as created
   *  (the user may have edited the prefill before submitting). */
  onCreated: (customerNo: string, customerName?: string) => void;
  onCancel: () => void;
}

const AddCustomerModal: React.FC<AddCustomerModalProps> = ({
  initialName,
  initialEmail,
  initialAddress1,
  initialAddress2,
  initialCity,
  initialState,
  initialZip,
  initialPhone,
  onCreated,
  onCancel,
}) => {
  const [sageEnabled, setSageEnabled] = useState<boolean | null>(null);
  const [salespersons, setSalespersons] = useState<SalespersonOption[]>([]);

  const [name, setName] = useState(initialName ?? '');
  const [salespersonNo, setSalespersonNo] = useState('');
  const [address1, setAddress1] = useState(initialAddress1 ?? '');
  const [address2, setAddress2] = useState(initialAddress2 ?? '');
  const [city, setCity] = useState(initialCity ?? '');
  const [state, setState] = useState(initialState ?? '');
  const [zip, setZip] = useState(initialZip ?? '');
  // Portal country code ('US' | 'CA') — drives the ZIP/Postal + State/Province
  // labels and the postal-code validation rule. Not stored in Sage (the
  // customer import has no CountryCode); validation/label UX only.
  const [country, setCountry] = useState('US');
  const [phone, setPhone] = useState(initialPhone ?? '');
  // A ChemMasters employee address is never the buyer's: Merlin's
  // proposed_new_customer on a forwarded PO can carry the forwarder, and a
  // customer row with that email would bind every later forward to it.
  const [email, setEmail] = useState(
    initialEmail && !isInternalEmail(initialEmail) ? initialEmail : '',
  );

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 409 exact-name duplicate — offer the explicit "create anyway" path. */
  const [duplicateDetail, setDuplicateDetail] = useState<string | null>(null);
  /** Success: the allocated account number. */
  const [createdNo, setCreatedNo] = useState<string | null>(null);

  useEffect(() => {
    portalService
      .getPortalConfig()
      .then((cfg) => setSageEnabled(Boolean(cfg.sage_edit_enabled)))
      .catch(() => setSageEnabled(false));
    portalService
      .listSalespersons()
      .then(setSalespersons)
      .catch(() => setSalespersons([]));
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onCancel();
  };

  const submit = async (allowDuplicate: boolean) => {
    const internal = findInternalAddresses(email);
    if (internal.length > 0) {
      setError(
        `${internal.join(', ')} is a ChemMasters employee address and cannot be ` +
          "a customer's email. Use the buyer's own contact address from the PO, " +
          'or leave the email blank.',
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    setDuplicateDetail(null);
    try {
      const res = await portalService.createSageCustomer({
        customer_name: name.trim(),
        salesperson_no: salespersonNo || null,
        address_line1: address1 || null,
        address_line2: address2 || null,
        city: city || null,
        state: state || null,
        zip_code: zip || null,
        country_code: country,
        telephone_no: phone || null,
        email_address: email || null,
        allow_duplicate_name: allowDuplicate,
      });
      if (res.success && res.customer_no) {
        setCreatedNo(res.customer_no);
      } else {
        setError(res.error_message || 'Sage rejected the customer create.');
      }
    } catch (err: unknown) {
      const ax = err as {
        response?: { status?: number; data?: { detail?: string } };
      };
      const detail = ax.response?.data?.detail;
      if (ax.response?.status === 409 && detail) {
        setDuplicateDetail(detail);
      } else {
        setError(detail || 'Failed to create the customer. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success panel ──
  if (createdNo) {
    return (
      <div className={styles.overlay} onKeyDown={handleKeyDown}>
        <div className={styles.modal} role="dialog" aria-label="Customer created">
          <div className={styles.header}>
            <span className={styles.headerTitle}>Customer queued to Sage</span>
          </div>
          <div className={styles.body}>
            <div
              data-testid="sage-customer-created"
              style={{ textAlign: 'center', padding: '18px 8px' }}
            >
              <div style={{ fontSize: 14, color: '#166534', marginBottom: 8 }}>
                ✓ New Sage account created
              </div>
              <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: 1 }}>
                {createdNo}
              </div>
              <div style={{ fontSize: 13, color: '#374151', marginTop: 10 }}>
                {name.trim()}
              </div>
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 12 }}>
                Queued to Sage — it usually lands within ~2 minutes and is
                red-flagged if it doesn't. The account appears in customer
                search once Sage confirms.
              </div>
            </div>
          </div>
          <div className={styles.footer}>
            <button
              type="button"
              className={styles.submitBtn}
              onClick={() => onCreated(createdNo, name.trim() || undefined)}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isCA = country === 'CA';

  return (
    <div className={styles.overlay} onKeyDown={handleKeyDown}>
      <div className={styles.modal} role="dialog" aria-label="Add new customer">
        <div className={styles.header}>
          <span className={styles.headerTitle}>Add New Customer</span>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onCancel}
            title="Close"
            data-action="customer_add_close"
            data-outcome="close"
          >
            &times;
          </button>
        </div>

        <div className={styles.body}>
          {sageEnabled === false ? (
            <div
              data-testid="sage-create-disabled"
              style={{ fontSize: 13, color: '#6b7280', padding: '10px 4px' }}
            >
              Customer creation isn&apos;t enabled in this environment. Please
              create the customer in Sage and wait for it to propagate over.
            </div>
          ) : (
            <>
              <div className={styles.fieldGroup}>
                <label className={`${styles.label} ${styles.required}`}>
                  Company name
                </label>
                <input
                  className={styles.input}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Zenith Coatings"
                  autoFocus
                />
                {name.trim() && (
                  <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 3 }}>
                    Account number is assigned automatically from the name
                    (next free number).
                  </div>
                )}
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.label}>Salesperson</label>
                <select
                  className={styles.select}
                  value={salespersonNo}
                  onChange={(e) => setSalespersonNo(e.target.value)}
                >
                  <option value="">Me (automatic)</option>
                  {salespersons.map((sp) => (
                    <option key={sp.salesperson_no} value={sp.salesperson_no}>
                      {sp.name} ({sp.salesperson_no})
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.sectionTitle}>
                Billing address (optional)
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Country</label>
                <select
                  className={styles.select}
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  data-testid="customer-country-select"
                >
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                </select>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Address line 1</label>
                <input
                  className={styles.input}
                  value={address1}
                  onChange={(e) => setAddress1(e.target.value)}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Address line 2</label>
                <input
                  className={styles.input}
                  value={address2}
                  onChange={(e) => setAddress2(e.target.value)}
                />
              </div>
              <div className={styles.row3}>
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>City</label>
                  <input
                    className={styles.input}
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>{isCA ? 'Province' : 'State'}</label>
                  <input
                    className={styles.input}
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    maxLength={2}
                    placeholder={isCA ? 'ON' : 'OH'}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>{isCA ? 'Postal Code' : 'ZIP'}</label>
                  <input
                    className={styles.input}
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    placeholder={isCA ? 'A1A 1A1' : ''}
                  />
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>Phone</label>
                  <input
                    className={styles.input}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.label}>Email</label>
                  <input
                    className={styles.input}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              {/* Duplicate-name 409 — explicit create-anyway path */}
              {duplicateDetail && (
                <div
                  data-testid="duplicate-customer-warning"
                  style={{
                    background: '#fffbeb',
                    border: '1px solid #fcd34d',
                    borderRadius: 6,
                    padding: '10px 12px',
                    fontSize: 12.5,
                    color: '#92400e',
                    marginTop: 8,
                  }}
                >
                  <div style={{ marginBottom: 6 }}>⚠ {duplicateDetail}</div>
                  <button
                    type="button"
                    onClick={() => void submit(true)}
                    disabled={submitting}
                    style={{
                      background: 'none',
                      border: '1px solid #d97706',
                      color: '#92400e',
                      borderRadius: 4,
                      padding: '4px 10px',
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    This is a different company — create anyway
                  </button>
                </div>
              )}

              {error && (
                <div
                  data-testid="add-customer-error"
                  style={{ color: '#b91c1c', fontSize: 12.5, marginTop: 8 }}
                >
                  ⚠ {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onCancel}
            disabled={submitting}
          >
            {sageEnabled === false ? 'Close' : 'Cancel'}
          </button>
          {sageEnabled !== false && (
            <button
              type="button"
              className={styles.submitBtn}
              onClick={() => void submit(false)}
              disabled={submitting || !name.trim() || sageEnabled === null}
              data-testid="add-customer-submit"
            >
              {submitting ? 'Creating…' : 'Create in Sage'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddCustomerModal;
